import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../services/api';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import '../styles/AdminDashboard.css';

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
const getEstadoBadge = (estado) => {
  const map = {
    activo:     { cls: 'badge-success',   text: '✓ Activo' },
    suspendido: { cls: 'badge-danger',    text: '⛔ Suspendido' },
    vencido:    { cls: 'badge-warning',   text: '⚠️ Vencido' },
    trial:      { cls: 'badge-info',      text: '🆓 Trial' },
  };
  return map[estado] || { cls: 'badge-secondary', text: estado };
};

const getDiasRestantes = (fechaFin) => {
  if (!fechaFin) return { texto: 'Sin fecha', cls: '' };
  const diff = Math.ceil((new Date(fechaFin) - new Date()) / 86400000);
  if (diff < 0)  return { texto: 'Vencido',     cls: 'danger-text' };
  if (diff <= 3) return { texto: `${diff}d ⚠️`, cls: 'warning-text' };
  return { texto: `${diff} días`,               cls: '' };
};

const EMOJIS_NEGOCIO = ['🏢','🦷','🩺','✂️','💆','🐾','🏃','🥗','🧠','👁️','💈','💅','🧴','💊','🏋️','🎨','📚','🍽️','🔧','🚗'];
const TIPO_NEGOCIO_INICIAL = { nombre: '', icono: '🏢', orden: 0, activo: true };

const NUEVO_PLAN_INICIAL = {
  nombre: '', descripcion: '', precio_mensual: '',
  max_usuarios: 1, max_clientes: 100, max_servicios: 10,
  max_productos: 0, tiene_sucursales: false,
  tiene_profesionales: false, max_profesionales: 0,
  tiene_reportes_avanzados: false,
};

const PLAN_COLORS = ['#6366f1', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

const PERIODOS = [
  [7,'7 días'],[15,'15 días'],[30,'30 días (1 mes)'],
  [60,'60 días'],[90,'3 meses'],[180,'6 meses'],[365,'1 año'],
];

/* ── StatCard ── */
const StatCard = ({ icon, label, value, sub, color = '#6366f1' }) => (
  <div className="adm-stat-card">
    <div className="adm-stat-icon" style={{ background: `${color}18`, color }}>{icon}</div>
    <div className="adm-stat-body">
      <span className="adm-stat-value">{value}</span>
      <span className="adm-stat-label">{label}</span>
      {sub && <span className="adm-stat-sub">{sub}</span>}
    </div>
  </div>
);

/* ── PlanBar ── */
const PlanBar = ({ nombre, count, total, color, precio }) => {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="adm-plan-bar">
      <div className="adm-plan-bar__head">
        <span className="adm-plan-bar__name">{nombre}</span>
        <span className="adm-plan-bar__meta">{count} empresa{count !== 1 ? 's' : ''} · ${precio}/mes</span>
      </div>
      <div className="adm-plan-bar__track">
        <div className="adm-plan-bar__fill" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="adm-plan-bar__pct">{pct}%</span>
    </div>
  );
};

/* ─────────────────────────────────────────────
   Componente principal
───────────────────────────────────────────── */
const AdminDashboard = () => {
  const [tenants, setTenants]   = useState([]);
  const [planes, setPlanes]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('todos');

  const [showPlanModal,      setShowPlanModal]      = useState(false);
  const [showPagoModal,      setShowPagoModal]      = useState(false);
  const [showNuevaSuscModal, setShowNuevaSuscModal] = useState(false);
  const [showTiposModal,     setShowTiposModal]     = useState(false);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);

  const [nuevoPlan, setNuevoPlan] = useState(NUEVO_PLAN_INICIAL);
  const [pagoData,  setPagoData]  = useState({ dias: 30, plan_id: '' });
  const [nuevaEmpresa, setNuevaEmpresa] = useState({ nombre_negocio: '', tipo_negocio: '', email: '', telefono: '', plan_id: '' });

  const [tiposNegocio,   setTiposNegocio]   = useState([]);
  const [tipoForm,       setTipoForm]       = useState(TIPO_NEGOCIO_INICIAL);
  const [tipoEditando,   setTipoEditando]   = useState(null);
  const [tipoSaving,     setTipoSaving]     = useState(false);
  const [tipoSubModal,   setTipoSubModal]   = useState(false); // crear/editar dentro del modal
  const [tipoConfirmDel, setTipoConfirmDel] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);

  useEffect(() => { fetchTenants(); fetchPlanes(); fetchTiposNegocio(); }, []);

  const fetchTenants = async () => {
    try {
      const { data } = await api.get('/admin/tenants');
      setTenants(data);
    } catch { toast.error('Error al cargar empresas'); }
    finally  { setLoading(false); }
  };

  const fetchPlanes = async () => {
    try { const { data } = await api.get('/planes'); setPlanes(data); }
    catch (e) { console.error(e); }
  };

  const fetchTiposNegocio = async () => {
    try { const { data } = await api.get('/tipos-negocio/admin/all'); setTiposNegocio(data); }
    catch (e) { console.error(e); }
  };

  const openCrearTipo = () => {
    setTipoEditando(null);
    setTipoForm(TIPO_NEGOCIO_INICIAL);
    setTipoSubModal(true);
  };

  const openEditarTipo = (t) => {
    setTipoEditando(t.id);
    setTipoForm({ nombre: t.nombre, icono: t.icono || '🏢', orden: t.orden ?? 0, activo: t.activo === 1 || t.activo === true });
    setTipoSubModal(true);
  };

  const guardarTipo = async (e) => {
    e.preventDefault();
    if (!tipoForm.nombre.trim()) { toast.error('El nombre es requerido'); return; }
    setTipoSaving(true);
    try {
      if (tipoEditando) {
        await api.put(`/tipos-negocio/admin/${tipoEditando}`, tipoForm);
        toast.success('Tipo actualizado');
      } else {
        await api.post('/tipos-negocio/admin', tipoForm);
        toast.success('Tipo creado');
      }
      setTipoSubModal(false);
      fetchTiposNegocio();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally { setTipoSaving(false); }
  };

  const toggleTipo = async (t) => {
    try {
      await api.put(`/tipos-negocio/admin/${t.id}`, { activo: !(t.activo === 1 || t.activo === true) });
      fetchTiposNegocio();
    } catch { toast.error('Error al cambiar estado'); }
  };

  const eliminarTipo = async (id) => {
    try {
      await api.delete(`/tipos-negocio/admin/${id}`);
      toast.success('Eliminado');
      setTipoConfirmDel(null);
      fetchTiposNegocio();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al eliminar');
      setTipoConfirmDel(null);
    }
  };

  const registrarPago = async () => {
    try {
      await api.post(`/admin/tenants/${empresaSeleccionada.id}/pago`, pagoData);
      toast.success(`✅ Pago registrado para ${empresaSeleccionada.nombre_negocio}`);
      setShowPagoModal(false);
      fetchTenants();
    } catch { toast.error('Error al registrar pago'); }
  };

  const crearNuevaEmpresa = async () => {
    if (!nuevaEmpresa.nombre_negocio || !nuevaEmpresa.email || !nuevaEmpresa.plan_id) {
      toast.warning('Nombre, email y plan son requeridos');
      return;
    }
    try {
      await api.post('/admin/tenants/crear', { ...nuevaEmpresa, dias: 7 });
      toast.success(`✅ Empresa "${nuevaEmpresa.nombre_negocio}" creada — 7 días activos`);
      setShowNuevaSuscModal(false);
      setNuevaEmpresa({ nombre_negocio: '', tipo_negocio: '', email: '', telefono: '', plan_id: '' });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear la empresa');
    }
  };

  const suspenderEmpresa = (id) => {
    setConfirmAction({ onConfirm: async () => {
      try { await api.post(`/admin/tenants/${id}/suspender`); toast.success('Empresa suspendida'); fetchTenants(); }
      catch { toast.error('Error al suspender'); }
    }});
  };

  const activarEmpresa = async (id) => {
    try { await api.post(`/admin/tenants/${id}/activar`); toast.success('Empresa activada'); fetchTenants(); }
    catch { toast.error('Error al activar'); }
  };

  const crearPlan = async () => {
    try {
      await api.post('/planes/admin', nuevoPlan);
      toast.success('📦 Plan creado correctamente');
      setShowPlanModal(false);
      setNuevoPlan(NUEVO_PLAN_INICIAL);
      fetchPlanes();
    } catch { toast.error('Error al crear plan'); }
  };

  /* ── Métricas derivadas ── */
  const stats = useMemo(() => {
    const activos     = tenants.filter(t => t.estado_suscripcion === 'activo').length;
    const suspendidos = tenants.filter(t => t.estado_suscripcion === 'suspendido').length;
    const mrr         = tenants
      .filter(t => t.estado_suscripcion === 'activo')
      .reduce((s, t) => s + (parseFloat(t.plan_precio) || 0), 0);
    const proxVencer  = tenants.filter(t => {
      if (!t.fecha_fin_suscripcion) return false;
      const d = Math.ceil((new Date(t.fecha_fin_suscripcion) - new Date()) / 86400000);
      return d >= 0 && d <= 7;
    });
    return { total: tenants.length, activos, suspendidos, mrr, proxVencer };
  }, [tenants]);

  const planesStats = useMemo(() => {
    const map = {};
    tenants.forEach(t => {
      const k = t.plan_nombre || 'Sin plan';
      if (!map[k]) map[k] = { nombre: k, count: 0, precio: t.plan_precio || 0, empresas: [] };
      map[k].count++;
      map[k].empresas.push(t.nombre_negocio);
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [tenants]);

  const mrrPotencial = useMemo(() =>
    tenants
      .filter(t => t.estado_suscripcion !== 'activo')
      .reduce((s, t) => s + (parseFloat(t.plan_precio) || 0), 0),
  [tenants]);

  const tenantsFiltrados = useMemo(() =>
    tenants.filter(t => {
      const q = busqueda.toLowerCase();
      const matchQ = !busqueda ||
        t.nombre_negocio?.toLowerCase().includes(q) ||
        t.email?.toLowerCase().includes(q) ||
        t.codigo_tenant?.toLowerCase().includes(q);
      const matchE = filtroEstado === 'todos' || t.estado_suscripcion === filtroEstado;
      return matchQ && matchE;
    }),
  [tenants, busqueda, filtroEstado]);

  if (loading) return <Layout><div className="loading-screen">Cargando panel...</div></Layout>;

  return (
    <Layout>
      <div className="admin-dashboard">

        {/* ── Header ── */}
        <div className="adm-header">
          <div>
            <h2 className="adm-title">👑 Panel Super Admin</h2>
            <p className="adm-subtitle">Gestiona empresas, suscripciones y planes</p>
          </div>
          <div className="adm-header-actions">
            <button className="btn btn-secondary" onClick={() => setShowNuevaSuscModal(true)}>
              💳 Nueva Suscripción
            </button>
            <button className="btn btn-secondary" onClick={() => setShowTiposModal(true)}>
              🏷️ Tipos de Negocio
            </button>
            <button className="btn btn-primary" onClick={() => setShowPlanModal(true)}>
              📦 Crear Nuevo Plan
            </button>
          </div>
        </div>

        {/* ── Stats ── */}
        <div className="adm-stats-grid">
          <StatCard icon="🏢" label="Empresas Totales"   value={stats.total}               color="#6366f1" />
          <StatCard icon="✅" label="Activas"            value={stats.activos}              color="#10b981"
            sub={`MRR: $${stats.mrr.toFixed(2)}`} />
          <StatCard icon="⚠️" label="Por vencer (7 días)" value={stats.proxVencer.length}  color="#f59e0b"
            sub={stats.proxVencer.map(t => t.nombre_negocio).join(', ') || '—'} />
          <StatCard icon="⛔" label="Suspendidas"        value={stats.suspendidos}          color="#ef4444" />
          <StatCard icon="💰" label="MRR Actual"         value={`$${stats.mrr.toFixed(2)}`} color="#10b981" />
          <StatCard icon="📦" label="Planes disponibles" value={planes.length}              color="#3b82f6" />
        </div>

        {/* ── Analytics ── */}
        <div className="adm-analytics-grid">

          {/* Planes más adquiridos */}
          <div className="adm-card">
            <div className="adm-card-head">
              <span className="adm-card-title">📊 Planes más adquiridos</span>
              <span className="adm-card-badge">{tenants.length} total</span>
            </div>
            <div className="adm-plan-bars">
              {planesStats.length === 0
                ? <p className="adm-empty">Sin datos</p>
                : planesStats.map((p, i) => (
                    <PlanBar key={p.nombre} {...p} total={tenants.length}
                      color={PLAN_COLORS[i % PLAN_COLORS.length]} />
                  ))
              }
            </div>
          </div>

          {/* Empresas por plan */}
          <div className="adm-card">
            <div className="adm-card-head">
              <span className="adm-card-title">🏢 Empresas por plan</span>
            </div>
            <div className="adm-plan-detail">
              {planesStats.map((p, i) => (
                <div key={p.nombre} className="adm-plan-detail__item">
                  <div className="adm-plan-detail__head">
                    <span className="adm-plan-detail__dot"
                      style={{ background: PLAN_COLORS[i % PLAN_COLORS.length] }} />
                    <strong>{p.nombre}</strong>
                    <span className="adm-plan-detail__count">{p.count}</span>
                  </div>
                  <div className="adm-plan-detail__names">
                    {p.empresas.map(e => <span key={e} className="adm-empresa-chip">{e}</span>)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Análisis y oportunidades */}
          <div className="adm-card adm-card--full">
            <div className="adm-card-head">
              <span className="adm-card-title">🔮 Análisis y oportunidades</span>
            </div>
            <div className="adm-insights-grid">

              <div className="adm-insight">
                <div className="adm-insight__icon" style={{ color: '#f59e0b' }}>⚠️</div>
                <div>
                  <div className="adm-insight__title">Próximas a vencer (7 días)</div>
                  <div className="adm-insight__body">
                    {stats.proxVencer.length === 0
                      ? <span className="adm-ok">Ninguna empresa vence pronto 🎉</span>
                      : stats.proxVencer.map(t => (
                          <div key={t.id} className="adm-insight__row">
                            <span>{t.nombre_negocio}</span>
                            <span className="adm-insight__tag warning">
                              {getDiasRestantes(t.fecha_fin_suscripcion).texto}
                            </span>
                          </div>
                        ))
                    }
                  </div>
                </div>
              </div>

              <div className="adm-insight">
                <div className="adm-insight__icon" style={{ color: '#6366f1' }}>💡</div>
                <div>
                  <div className="adm-insight__title">MRR actual vs potencial</div>
                  <div className="adm-insight__body">
                    <div className="adm-mrr-row">
                      <span>MRR real</span>
                      <strong style={{ color: '#10b981' }}>${stats.mrr.toFixed(2)}</strong>
                    </div>
                    <div className="adm-mrr-row">
                      <span>Pérdida por inactivas</span>
                      <strong style={{ color: '#ef4444' }}>-${mrrPotencial.toFixed(2)}</strong>
                    </div>
                    <div className="adm-mrr-row adm-mrr-row--total">
                      <span>Potencial total</span>
                      <strong style={{ color: '#6366f1' }}>${(stats.mrr + mrrPotencial).toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <div className="adm-insight">
                <div className="adm-insight__icon" style={{ color: '#10b981' }}>📈</div>
                <div>
                  <div className="adm-insight__title">Plan más popular</div>
                  <div className="adm-insight__body">
                    {planesStats[0]
                      ? <>
                          <div className="adm-top-plan">
                            <span className="adm-top-plan__name">{planesStats[0].nombre}</span>
                            <span className="adm-top-plan__count">
                              {planesStats[0].count} empresa{planesStats[0].count !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="adm-insight__hint">
                            Representa el {tenants.length > 0
                              ? Math.round((planesStats[0].count / tenants.length) * 100) : 0}% de clientes.
                            {planesStats[0].count / tenants.length > 0.6
                              ? ' Alta concentración — considera un tier intermedio.'
                              : ' Distribución saludable entre planes.'}
                          </div>
                        </>
                      : <span className="adm-ok">Sin datos aún</span>
                    }
                  </div>
                </div>
              </div>

              <div className="adm-insight">
                <div className="adm-insight__icon" style={{ color: '#3b82f6' }}>🎯</div>
                <div>
                  <div className="adm-insight__title">Tasa de retención</div>
                  <div className="adm-insight__body">
                    <div className="adm-retention">
                      <span className="adm-retention__pct" style={{ color: '#10b981' }}>
                        {tenants.length > 0 ? Math.round((stats.activos / tenants.length) * 100) : 0}%
                      </span>
                      <span className="adm-retention__label">empresas activas</span>
                    </div>
                    <div className="adm-insight__hint">
                      {stats.suspendidos > 0
                        ? `${stats.suspendidos} empresa${stats.suspendidos > 1 ? 's' : ''} suspendida${stats.suspendidos > 1 ? 's' : ''} — oportunidad de reactivación.`
                        : 'Sin suspensiones. ¡Excelente retención!'
                      }
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ── Tabla de empresas ── */}
        <div className="adm-card">
          <div className="adm-card-head">
            <span className="adm-card-title">📋 Empresas Registradas</span>
            <div className="adm-table-filters">
              <input className="adm-search" placeholder="Buscar empresa, email, código..."
                value={busqueda} onChange={e => setBusqueda(e.target.value)} />
              <select className="adm-filter-select" value={filtroEstado}
                onChange={e => setFiltroEstado(e.target.value)}>
                <option value="todos">Todos los estados</option>
                <option value="activo">Activos</option>
                <option value="vencido">Vencidos</option>
                <option value="suspendido">Suspendidos</option>
                <option value="trial">Trial</option>
              </select>
            </div>
          </div>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Código</th><th>Empresa</th><th>Plan</th>
                  <th>Estado</th><th>Vence</th><th>Clientes</th>
                  <th>Citas</th><th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tenantsFiltrados.length === 0 && (
                  <tr><td colSpan="8" className="adm-empty">Sin resultados</td></tr>
                )}
                {tenantsFiltrados.map(tenant => {
                  const estado = getEstadoBadge(tenant.estado_suscripcion);
                  const dias   = getDiasRestantes(tenant.fecha_fin_suscripcion);
                  return (
                    <tr key={tenant.id}
                      className={tenant.estado_suscripcion === 'suspendido' ? 'suspended-row' : ''}>
                      <td><code>{tenant.codigo_tenant}</code></td>
                      <td>
                        <strong>{tenant.nombre_negocio}</strong><br />
                        <small>{tenant.email}</small>
                      </td>
                      <td><span className="plan-badge">{tenant.plan_nombre}</span></td>
                      <td><span className={`badge ${estado.cls}`}>{estado.text}</span></td>
                      <td className={dias.cls}>{dias.texto}</td>
                      <td>{tenant.total_clientes || 0}</td>
                      <td>{tenant.total_citas || 0}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => {
                              setEmpresaSeleccionada(tenant);
                              setPagoData({ dias: 30, plan_id: planes[0]?.id || '' });
                              setShowPagoModal(true);
                            }}
                            title="Registrar pago"
                            style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                          >
                            💳
                          </button>
                          {tenant.estado_suscripcion === 'suspendido'
                            ? <button
                                className="btn btn-sm btn-success"
                                onClick={() => activarEmpresa(tenant.id)}
                                title="Activar empresa"
                                style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                              >✓</button>
                            : <button
                                className="btn btn-sm btn-warning"
                                onClick={() => suspenderEmpresa(tenant.id)}
                                title="Suspender empresa"
                                style={{ padding: '4px 8px', fontSize: '0.75rem' }}
                              >⛔</button>
                          }
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* ════ MODAL: Crear Plan ════ */}
        {showPlanModal && (
          <div className="modal-overlay">
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                padding: '1.5rem 1.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    width: '42px', height: '42px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem',
                  }}>
                    📊
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                      Crear Nuevo Plan
                    </h3>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                      Completa los datos para agregar un nuevo plan
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPlanModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '32px', height: '32px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>
              <form onSubmit={e => { e.preventDefault(); crearPlan(); }}>
                <div className="modal-body">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Nombre del Plan *</label>
                      <input type="text" className="form-input" required
                        value={nuevoPlan.nombre}
                        onChange={e => setNuevoPlan({ ...nuevoPlan, nombre: e.target.value })} />
                    </div>
                    <div className="form-group">
                      <label>Precio Mensual (USD) *</label>
                      <input type="number" step="0.01" className="form-input" required
                        value={nuevoPlan.precio_mensual}
                        onChange={e => setNuevoPlan({ ...nuevoPlan, precio_mensual: e.target.value })} />
                    </div>
                    <div className="form-group full-width">
                      <label>Descripción</label>
                      <textarea className="form-textarea" rows="2"
                        value={nuevoPlan.descripcion}
                        placeholder="Describe las características del plan..."
                        onChange={e => setNuevoPlan({ ...nuevoPlan, descripcion: e.target.value })} />
                    </div>
                    {[['max_usuarios','Máx. Usuarios'],['max_clientes','Máx. Clientes'],
                      ['max_servicios','Máx. Servicios'],['max_profesionales','Máx. Profesionales']
                    ].map(([k, lbl]) => (
                      <div className="form-group" key={k}>
                        <label>{lbl}</label>
                        <input type="number" className="form-input" value={nuevoPlan[k]}
                          onChange={e => setNuevoPlan({ ...nuevoPlan, [k]: parseInt(e.target.value) })} />
                      </div>
                    ))}
                    {[['tiene_sucursales','🏢 Multi-sucursal'],
                      ['tiene_profesionales','👥 Gestión de Profesionales'],
                      ['tiene_reportes_avanzados','📊 Reportes Avanzados']
                    ].map(([k, lbl]) => (
                      <div className="form-group checkbox-group" key={k}>
                        <label>
                          <input type="checkbox" checked={nuevoPlan[k]}
                            onChange={e => setNuevoPlan({ ...nuevoPlan, [k]: e.target.checked })} />
                          {lbl}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary"
                    onClick={() => setShowPlanModal(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary">Crear Plan</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ════ MODAL: Pago empresa seleccionada ════ */}
        {showPagoModal && empresaSeleccionada && (
          <div className="modal-overlay">
            <div className="modal" onClick={e => e.stopPropagation()} style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                padding: '1.5rem 1.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    width: '42px', height: '42px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem',
                  }}>
                    📊
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                      Pago — {empresaSeleccionada.nombre_negocio}
                    </h3>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                      Registra el pago de suscripción
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPagoModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '32px', height: '32px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Plan a asignar</label>
                  <select className="form-select" value={pagoData.plan_id}
                    onChange={e => setPagoData({ ...pagoData, plan_id: e.target.value })}>
                    {planes.map(p => (
                      <option key={p.id} value={p.id}>{p.nombre} — ${p.precio_mensual}/mes</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Período de suscripción</label>
                  <select className="form-select" value={pagoData.dias}
                    onChange={e => setPagoData({ ...pagoData, dias: parseInt(e.target.value) })}>
                    {PERIODOS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div className="info-box">
                  <p>📌 La empresa quedará <strong>Activa</strong> y la suscripción se extenderá por el período seleccionado.</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowPagoModal(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={registrarPago}>Registrar Pago</button>
              </div>
            </div>
          </div>
        )}

        {/* ════ MODAL: Nueva Empresa ════ */}
        {showNuevaSuscModal && (
          <div className="modal-overlay">
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                padding: '1.5rem 1.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    width: '42px', height: '42px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem',
                  }}>
                    📊
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                      Nueva Empresa
                    </h3>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                      Completa los datos para agregar una nueva empresa
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNuevaSuscModal(false)}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '32px', height: '32px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Nombre del negocio *</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ej: Salón de Belleza Ana"
                      value={nuevaEmpresa.nombre_negocio}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, nombre_negocio: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Tipo de negocio</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ej: Peluquería, Clínica, Spa"
                      value={nuevaEmpresa.tipo_negocio}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, tipo_negocio: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="correo@empresa.com"
                      value={nuevaEmpresa.email}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, email: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Teléfono</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="+593 99 999 9999"
                      value={nuevaEmpresa.telefono}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, telefono: e.target.value })}
                    />
                  </div>
                  <div className="form-group full-width">
                    <label>Plan a asignar *</label>
                    <select
                      className="form-select"
                      value={nuevaEmpresa.plan_id}
                      onChange={e => setNuevaEmpresa({ ...nuevaEmpresa, plan_id: e.target.value })}
                    >
                      <option value="">— Selecciona un plan —</option>
                      {planes.map(p => (
                        <option key={p.id} value={p.id}>
                          {p.nombre} — ${p.precio_mensual}/mes
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="info-box" style={{ marginTop: '1rem' }}>
                  <p>⏱️ La empresa quedará <strong>Activa por 7 días</strong> hasta que el cliente realice su primer pago. Se generará automáticamente un código de acceso.</p>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setShowNuevaSuscModal(false)}>
                  Cancelar
                </button>
                <button className="btn btn-primary" onClick={crearNuevaEmpresa}>
                  🏢 Crear empresa y activar 7 días
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ════ MODAL: Tipos de Negocio ════ */}
        {showTiposModal && (
          <div className="modal-overlay">
            <div className="modal modal-lg" onClick={e => e.stopPropagation()} style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <div style={{
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
                padding: '1.5rem 1.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    background: 'rgba(255,255,255,0.2)',
                    borderRadius: '10px',
                    width: '42px', height: '42px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3rem',
                  }}>
                    📊
                  </div>
                  <div>
                    <h3 style={{ margin: 0, color: 'white', fontSize: '1.15rem', fontWeight: '700' }}>
                      Tipos de Negocio
                    </h3>
                    <p style={{ margin: 0, color: 'rgba(255,255,255,0.75)', fontSize: '0.8rem' }}>
                      Gestiona los tipos de negocio disponibles
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => { setShowTiposModal(false); setTipoSubModal(false); }}
                  style={{
                    background: 'rgba(255,255,255,0.15)',
                    border: 'none',
                    borderRadius: '8px',
                    color: 'white',
                    width: '32px', height: '32px',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >✕</button>
              </div>

              {!tipoSubModal ? (
                <>
                  <div className="modal-body">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
                      <button className="btn btn-primary" style={{ fontSize: '0.875rem' }} onClick={openCrearTipo}>
                        + Nuevo tipo
                      </button>
                    </div>
                    <table className="table">
                      <thead>
                        <tr>
                          <th style={{ width: '60px', textAlign: 'center' }}>Icono</th>
                          <th>Nombre</th>
                          <th style={{ width: '70px', textAlign: 'center' }}>Orden</th>
                          <th style={{ width: '100px' }}>Estado</th>
                          <th style={{ width: '130px' }}>Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tiposNegocio.length === 0 && (
                          <tr><td colSpan={5} style={{ textAlign: 'center', color: '#9ca3af', padding: '1.5rem' }}>Sin tipos de negocio</td></tr>
                        )}
                        {tiposNegocio.map(t => {
                          const activo = t.activo === 1 || t.activo === true;
                          return (
                            <tr key={t.id}>
                              <td style={{ fontSize: '1.4rem', textAlign: 'center' }}>{t.icono || '🏢'}</td>
                              <td style={{ fontWeight: 500 }}>{t.nombre}</td>
                              <td style={{ textAlign: 'center', color: '#6b7280' }}>{t.orden ?? 0}</td>
                              <td>
                                <button
                                  onClick={() => toggleTipo(t)}
                                  style={{
                                    padding: '0.2rem 0.6rem', borderRadius: '999px', border: 'none',
                                    cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
                                    background: activo ? '#dcfce7' : '#f3f4f6',
                                    color: activo ? '#166534' : '#6b7280',
                                  }}
                                >{activo ? 'Activo' : 'Inactivo'}</button>
                              </td>
                              <td>
                                <div style={{ display: 'flex', gap: '0.35rem' }}>
                                  <button className="btn btn-secondary" style={{ padding: '3px 10px', fontSize: '0.75rem' }} onClick={() => openEditarTipo(t)}>Editar</button>
                                  <button
                                    style={{ padding: '3px 10px', fontSize: '0.75rem', background: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '8px', cursor: 'pointer' }}
                                    onClick={() => setTipoConfirmDel(t)}
                                  >Eliminar</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>

                    {/* Confirm delete inline */}
                    {tipoConfirmDel && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#fee2e2', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>¿Eliminar <strong>{tipoConfirmDel.nombre}</strong>?</span>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="btn btn-secondary" style={{ fontSize: '0.8rem' }} onClick={() => setTipoConfirmDel(null)}>Cancelar</button>
                          <button style={{ padding: '0.4rem 1rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.8rem' }} onClick={() => eliminarTipo(tipoConfirmDel.id)}>Eliminar</button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setShowTiposModal(false)}>Cerrar</button>
                  </div>
                </>
              ) : (
                <form onSubmit={guardarTipo}>
                  <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Nombre *</label>
                      <input className="form-input" value={tipoForm.nombre} required
                        onChange={e => setTipoForm(p => ({ ...p, nombre: e.target.value }))}
                        placeholder="Ej: Dentista" />
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Icono (emoji)</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                        <input className="form-input" value={tipoForm.icono} maxLength={4}
                          onChange={e => setTipoForm(p => ({ ...p, icono: e.target.value }))}
                          style={{ maxWidth: '72px', fontSize: '1.25rem', textAlign: 'center' }} />
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.2rem' }}>
                          {EMOJIS_NEGOCIO.map(em => (
                            <button key={em} type="button"
                              onClick={() => setTipoForm(p => ({ ...p, icono: em }))}
                              style={{
                                fontSize: '1.1rem', border: tipoForm.icono === em ? '2px solid #6366f1' : '2px solid #e5e7eb',
                                borderRadius: '8px', padding: '2px 5px', cursor: 'pointer',
                                background: tipoForm.icono === em ? '#ede9fe' : 'white'
                              }}
                            >{em}</button>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label className="form-label">Orden (menor = primero)</label>
                      <input className="form-input" type="number" min="0" value={tipoForm.orden}
                        onChange={e => setTipoForm(p => ({ ...p, orden: parseInt(e.target.value) || 0 }))} />
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input type="checkbox" checked={tipoForm.activo}
                        onChange={e => setTipoForm(p => ({ ...p, activo: e.target.checked }))} />
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Activo (visible en el registro)</span>
                    </label>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary" onClick={() => setTipoSubModal(false)}>← Volver</button>
                    <button type="submit" className="btn btn-primary" disabled={tipoSaving}>
                      {tipoSaving ? 'Guardando...' : (tipoEditando ? 'Guardar cambios' : 'Crear tipo')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}

      </div>

      {confirmAction && (
        <div className="modal-overlay">
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '420px', borderRadius: '16px', overflow: 'hidden' }}>
            <div style={{
              background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
              padding: '1.25rem 1.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                <h3 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: '700' }}>
                  Confirmar acción
                </h3>
              </div>
              <button
                onClick={() => setConfirmAction(null)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: 'none',
                  borderRadius: '8px',
                  color: 'white',
                  width: '28px', height: '28px',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
              >✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color: '#374151' }}>¿Confirmas esta acción?</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setConfirmAction(null)}>No</button>
              <button className="btn btn-primary" onClick={() => { confirmAction.onConfirm(); setConfirmAction(null); }}>Sí</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default AdminDashboard;
